import React, { FC, Fragment, ChangeEvent, useState } from 'react';
import { Paper } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { MutationFunction, QueryResult } from 'react-apollo';
import { pull, uniq } from 'lodash';

import {
  BatchDeleteGroupsComponent,
  Group,
  SearchGroupsDocument,
  SearchSortDirections,
  SearchSortFields,
} from 'api';
import { EmptyState, Queries } from 'components';
import { useNotify } from 'hooks';
import { adminGroups } from 'apollo/reducers';

import Columns from '../components/Groups/Columns';
import Header from '../components/Groups/Header';
import VirtualizedTable from '../VirtualizedTable';
import Loader from '../components/Groups/Loader';

interface IProps extends WithStyles<typeof styles> {}

const RESULTS_FROM = 0;
const SIZE = 24;

const styles = (theme: Theme) =>
  createStyles({
    column: {
      borderTop: `1px solid ${theme.custom.palette.grey3}`,
      borderBottom: `1px solid ${theme.custom.palette.grey3}`,
    },
  });

const Groups: FC<IProps> = ({ classes }) => {
  const [total, setTotal] = useState(null);
  const [updatedTotal, setUpdatedTotal] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  const actualTotal = updatedTotal || total;
  const notify = useNotify();

  const variables = {
    results_from: RESULTS_FROM,
    size: SIZE,
    auto_exclude: false,
    sort: SearchSortFields.Title,
    sort_direction: SearchSortDirections.Asc,
  };

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>, groups: Group[]) {
    const { checked } = event.target;
    const ids = checked ? groups.map(group => group.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function getColumns(groups: Group[]) {
    return Columns({
      checkedIds,
      className: classes.column,
      isAllChecked: allChecked,
      onAllChecked: event => handleAllChecked(event, groups),
      onChecked: handleChecked,
    });
  }

  function handleMutationCompleted() {
    notify.info({
      i18n: {
        i18nKey: 'admin:deleteGroupSuccessMessage',
        count: checkedIds.length,
        values: { checkedTotal: checkedIds.length },
      },
    });

    setUpdatedTotal(actualTotal - checkedIds.length);
    setCheckedIds([]);
  }

  function handleQueryCompleted(data: QueryResult['data']) {
    setTotal(data.search && data.search.count);
  }

  function handleAction(mutationFn: MutationFunction) {
    return mutationFn({
      update: (cache, result) => {
        adminGroups({
          cache,
          result,
          variables,
          query: SearchGroupsDocument,
        });
      },
      variables: {
        ids: checkedIds,
      },
    });
  }

  return (
    <Paper>
      <BatchDeleteGroupsComponent
        awaitRefetchQueries
        onError={notify.mutationError}
        onCompleted={handleMutationCompleted}>
        {mutationFn => (
          <Header
            total={actualTotal}
            checkedTotal={checkedIds.length}
            onAction={() => handleAction(mutationFn)}
          />
        )}
      </BatchDeleteGroupsComponent>
      <Queries.Search
        fetchPolicy="network-only"
        document={SearchGroupsDocument}
        variables={variables}
        loader={<Loader rows={8} />}
        onCompleted={handleQueryCompleted}>
        {({ count, exhausted, results, onLoadMore }) => (
          <Fragment>
            <VirtualizedTable
              columns={getColumns(results)}
              outerBorderWidth={2}
              spacing={1}
              headerHeight={50}
              rowHeight={75}
              total={count}
              rowCount={results.length}
              headerClassName={classes.column}
              isRowLoaded={({ index }: { index: number }) => !!results[index]}
              rowGetter={({ index }: { index: number }) => results[index]}
              onLoadMoreRows={onLoadMore}
              noRowsRenderer={() => <EmptyState name="groupsOwned" />}
            />
            {!exhausted && <Loader rows={1} />}
          </Fragment>
        )}
      </Queries.Search>
    </Paper>
  );
};

export default withStyles(styles)(Groups);
